import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import customerManufacturersApi from "../shared/api/customerManufacturersApi";
import customerCategoriesApi from "../shared/api/customerCategoriesApi";
import {
  CompaniesQuery,
  CompanyMachinesQuery,
  CompanyQueryParameter,
  CustomerCompanyState
} from "./models/models";
import customerCompaniesApi from "./api/customerCompaniesApi";
import customerPublicMachineryApi from "../machinery/api/customerPublicMachineryApi";
import { Machine } from "../machinery/models/models";
import customerMachineryApi from "../machinery/api/customerMachineryApi";

export const getCompanies = createAsyncThunk(
  "customerCompanies/getCompanies",
  async (query: CompaniesQuery) => {
    return customerCompaniesApi.getCompanies(query);
  }
);

export const getCompany = createAsyncThunk(
  "customerCompanies/getCompany",
  async (id: string) => {
    const [company, machines] = await Promise.all([
      customerCompaniesApi.getCompany(id),
      customerPublicMachineryApi.getPublickMachines({ customerId: id }) as Promise<Record<string, Machine[]>>,
    ]);

    company.hasMachines = !!Object.keys(machines)?.length;

    return { company, machines };
  }
);

export const getMachines = createAsyncThunk(
  "customerCompanies/getMachines",
  async (query: CompanyMachinesQuery) => {
    return customerPublicMachineryApi.getPublickMachines(query);
  }
);

export const getManufacturers = createAsyncThunk(
  "customerCompanies/getManufacturers",
  async () => {
    return customerManufacturersApi.getManufacturers();
  }
);

export const getCategories = createAsyncThunk(
  "customerCompanies/getCategories",
  async () => {
    return customerCategoriesApi.getCategories();
  }
);

export const getMachineManufacturers = createAsyncThunk(
  "customerCompanies/getMachineManufacturers",
  async (machineTypeId: string) => {
    return customerMachineryApi.getManufacturersById(machineTypeId);
  }
);

export const getMachineTypes = createAsyncThunk(
  "customerCompanies/getMachineTypes",
  async () => {
    return customerMachineryApi.getTypes();
  }
);

export const getMachineCounts = createAsyncThunk(
  "customerCompanies/getMachineCounts",
  async (customerIds: string[]) => {
    return customerPublicMachineryApi.getMachineCounts(customerIds);
  }
);

const initialState: CustomerCompanyState = {
  query: {
    search: undefined,
    sortBy: CompanyQueryParameter.name,
    sortDesc: false,
    pageNo: 1,
    pageSize: 20
  },
  companies: undefined,
  company: undefined,
  machinesQuery: {
    manufacturerId: undefined,
    typeId: undefined,
    search: undefined
  },
  machines: {},
  manufacturers: [],
  machineManufacturers: [],
  machineTypes: [],
  categories: [],
  countries: [],
  machineCounts: {},
  loading: false,
  tabLoading: false,
  success: undefined,
  error: undefined
};

const customerCompaniesSlice = createSlice({
  name: "customerCompanies",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    changeMachinesQuery: (state, action) => {
      state.machinesQuery = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompanies.fulfilled, (state, { payload }) => {
        state.companies = payload;
        state.loading = false;
      })
      .addCase(getCompanies.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCompany.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompany.fulfilled, (state, { payload }) => {
        state.company = payload.company;
        state.machines = payload.machines;
        state.loading = false;
      })
      .addCase(getCompany.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getMachines.pending, (state) => {
        state.tabLoading = true;
      })
      .addCase(getMachines.fulfilled, (state, { payload }) => {
        state.machines = payload;
        state.tabLoading = false;
      })
      .addCase(getMachines.rejected, (state, { error }) => {
        state.error = error;
        state.tabLoading = false;
      });

    builder
      .addCase(getManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
      });

    builder
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
      });

    builder
      .addCase(getMachineManufacturers.fulfilled, (state, { payload }) => {
        state.machineManufacturers = payload;
      });

    builder
      .addCase(getMachineTypes.fulfilled, (state, { payload }) => {
        state.machineTypes = payload.filter(x => x.name === "CNC Machine");
      });

    builder
      .addCase(getMachineCounts.fulfilled, (state, { payload }) => {
        state.machineCounts = { ...state.machineCounts, ...payload };
      });
  }
});

export const {
  changeQuery,
  changeMachinesQuery,
  setSuccess,
  setLoading,
  setError
} = customerCompaniesSlice.actions;

export default customerCompaniesSlice.reducer;
